// PayoutTab.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  Collapse,
  Typography,
  Grid,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import Token from "../../../util/Token";
import { getTeamPayout } from "src/react-query/endPoints";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useDomainConfig } from "src/hooks/useDomainConfig";

/**
 * Maps the payout_rule integer to a human-readable label.
 */
const PAYOUT_RULE_LABELS = {
  1: "Fixed Amount or % per Unit (higher)",
  2: "Fixed Amount or Fixed per Unit (higher)",
  3: "Fixed Amount + % per Unit",
  4: "Fixed Amount + Fixed per Unit",
  5: "% per Unit",
  6: "Fixed per Unit",
  7: "Fixed Amount per Month",
};

/**
 * A read-only field that mimics an MUI outlined TextField (shrunk label).
 * hint is rendered inline after the value on the same line (e.g. "2.36 % of the payment captured").
 */
const ReadOnlyField = ({ label, value, hint }) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      px: 2,
      pt: 1.25,
      pb: 1,
      position: "relative",
      backgroundColor: "background.paper",
      minHeight: 48,
    }}
  >
    {/* Floating label — identical to MUI TextField shrink behaviour */}
    <Typography
      component="span"
      sx={{
        position: "absolute",
        top: -8,
        left: 10,
        px: 0.5,
        backgroundColor: "background.paper",
        color: "text.secondary",
        lineHeight: 1,
        fontSize: "0.68rem",
        letterSpacing: "0.01em",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>

    {/* Value + inline hint on one line */}
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "nowrap" }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
        {value ?? "—"}
      </Typography>
      {hint && (
        <Typography
          component="span"
          variant="caption"
          sx={{ color: "text.disabled", whiteSpace: "nowrap", lineHeight: "inherit" }}
        >
          {hint}
        </Typography>
      )}
    </Box>
  </Box>
);


/**
 * Displays the financeConfig section for a team — only rendered for GP users.
 * Mirrors the exact field names and layout of TeamFinanceForm (read-only).
 */
const FinanceConfigSection = ({ financeConfig }) => {
  const [open, setOpen] = useState(false);

  if (!financeConfig) return null;

  const {
    charger_payment_details: cp,
    transaction_payment_details: tp,
    payout_details: pd,
    payment_gateway_details: pgd,
  } = financeConfig;

  const isMonthly = !!cp?.monthly_payment;

  const fmtCurrency = (val) =>
    val !== undefined && val !== null && !isNaN(parseFloat(val))
      ? `INR ${parseFloat(val).toFixed(2)}`
      : "—";
  const fmtPct = (val) =>
    val !== undefined && val !== null && !isNaN(parseFloat(val))
      ? `${parseFloat(val).toFixed(2)}`
      : "—";

  const payoutRuleLabel = pd?.payout_rule
    ? PAYOUT_RULE_LABELS[pd.payout_rule] || `Rule ${pd.payout_rule}`
    : "—";

  const commissionIsPercent = !!pd?.chargemiles_commission_in_percent;

  return (
    <Box sx={{ mt: 2 }}>
      {/* ── Accordion trigger ─────────────────────────────────── */}
      <Box
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          px: 1,
          py: 0.75,
          borderRadius: 1,
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          Finance Configuration
        </Typography>
        <IconButton
          size="small"
          sx={{
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Box>

      <Collapse in={open}>
        <Box sx={{ mt: 1 }}>
          <Grid container spacing={3}>

            {/* ═══ CHARGER PAYMENT SETTINGS ═══════════════════════════ */}
            <Grid item xs={12} sx={{ mb: 0 }}>
              <Typography sx={{ ml: 1 }} variant="subtitle1">
                CHARGER PAYMENT SETTINGS
              </Typography>
              <Divider className="text-muted" variant="fullWidth" />
            </Grid>

            {/* Monthly / Annual toggle (read-only chip) */}
            <Grid item xs={12} lg={4} md={4}>
              <ReadOnlyField
                label="Cost Type"
                value={isMonthly ? "Monthly Cost" : "Annual Cost"}
              />
            </Grid>

            {isMonthly ? (
              <>
                <Grid item xs={12} lg={4} md={4}>
                  <ReadOnlyField
                    label="Monthly Payment Amount (DC Charger)"
                    value={fmtCurrency(cp?.dc_charges_monthly)}
                  />
                </Grid>
                <Grid item xs={12} lg={4} md={4}>
                  <ReadOnlyField
                    label="Monthly Payment Amount (AC Charger)"
                    value={fmtCurrency(cp?.ac_charges_monthly)}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} lg={4} md={4}>
                  <ReadOnlyField
                    label="One Time Annual Payment Amount (DC Charger)"
                    value={fmtCurrency(cp?.dc_charges_annual)}
                  />
                </Grid>
                <Grid item xs={12} lg={4} md={4}>
                  <ReadOnlyField
                    label="One Time Annual Payment Amount (AC Charger)"
                    value={fmtCurrency(cp?.ac_charges_annual)}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} lg={4} md={4}>
              <ReadOnlyField
                label="Plus Taxes"
                value={fmtPct(cp?.tax)}
                hint="%"
              />
            </Grid>
            <Grid item xs={12} lg={4} md={4}>
              <ReadOnlyField
                label="Any Other Fixed Cost"
                value={fmtCurrency(cp?.other_fixed_cost)}
              />
            </Grid>

            {/* ═══ TRANSACTION PAYMENT SETTINGS ══════════════════════ */}
            <Grid item xs={12} sx={{ mb: 0, mt: 2 }}>
              <Typography sx={{ ml: 1 }} variant="subtitle1">
                TRANSACTION PAYMENT SETTINGS
              </Typography>
              <Divider className="text-muted" variant="fullWidth" />
            </Grid>

            <Grid item xs={12} lg={4} md={4}>
              <ReadOnlyField
                label="Payment Gateway Charges Bearer"
                value={tp?.pg_charges_bearer === "EV" ? "EV Driver" : tp?.pg_charges_bearer || "—"}
              />
            </Grid>
            <Grid item xs={12} lg={4} md={4}>
              <ReadOnlyField
                label="Payment Gateway Charges"
                value={fmtPct(tp?.payment_gateway_percent)}
                hint="% of the payment captured"
              />
            </Grid>
            <Grid item xs={12} lg={4} md={4}>
              <ReadOnlyField
                label="Plus Taxes"
                value={fmtPct(tp?.tax)}
                hint="%"
              />
            </Grid>

            {/* ═══ PAYOUT SETTINGS ════════════════════════════════════ */}
            <Grid item xs={12} sx={{ mb: 0, mt: 2 }}>
              <Typography sx={{ ml: 1 }} variant="subtitle1">
                PAYOUT SETTINGS
              </Typography>
              <Divider className="text-muted" variant="fullWidth" />
            </Grid>

            <Grid item xs={12} lg={4} md={4}>
              <ReadOnlyField
                label="Payout Calculations Rule"
                value={payoutRuleLabel}
              />
            </Grid>
            <Grid item xs={12} lg={4} md={4}>
              <ReadOnlyField
                label="Tax Bearer"
                value={pd?.tax_bearer === "CM" ? "Chargemiles" : pd?.tax_bearer || "—"}
              />
            </Grid>
            <Grid item xs={12} lg={4} md={4}>
              <ReadOnlyField
                label="Plus Taxes"
                value={fmtPct(pd?.tax)}
                hint="%"
              />
            </Grid>

            {/* Amik Commission sub-section */}
            <Grid item xs={12} lg={12} sx={{ mt: 1 }}>
              <Typography sx={{ ml: 1, fontWeight: 600 }} variant="body1">
                Amik Commission
              </Typography>
            </Grid>
            <Grid item xs={12} lg={4} md={4}>
              <ReadOnlyField
                label="Commission Type"
                value={commissionIsPercent ? "In Percentage" : "Fixed Cost"}
              />
            </Grid>
            <Grid item xs={12} lg={4} md={4}>
              {commissionIsPercent ? (
                <ReadOnlyField
                  label="Amik commission percent"
                  value={fmtPct(pd?.chargemiles_commission_percent)}
                  hint="% per unit"
                />
              ) : (
                <ReadOnlyField
                  label="Amik commission cost"
                  value={fmtCurrency(pd?.chargemiles_commission_cost)}
                  hint="per unit"
                />
              )}
            </Grid>

            {/* ═══ PAYMENT GATEWAY SETTINGS ═══════════════════════════ */}
            <Grid item xs={12} sx={{ mb: 0, mt: 2 }}>
              <Typography sx={{ ml: 1 }} variant="subtitle1">
                PAYMENT GATEWAY SETTINGS
              </Typography>
              <Divider className="text-muted" variant="fullWidth" />
            </Grid>

            <Grid item xs={12} lg={4} md={4}>
              <ReadOnlyField
                label="Payment Gateway"
                value={pgd?.name || pgd?.key_id || (pgd?.payment_gateway_id ? `Gateway #${pgd.payment_gateway_id}` : "—")}
              />
            </Grid>

          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
};

export const PayoutsTab = () => {
  const { teamId } = useSelectedTeam();
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [payoutData, setPayoutData] = useState(null);
  const [payoutError, setPayoutError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isGpUser, setIsGpUser] = useState(false);
  const domainConfig = useDomainConfig();

  // Detect GP user from JWT on mount
  useEffect(() => {
    const parsed = Token.parsedToken();
    if (parsed.success && parsed.data?.companyType === "GP") {
      setIsGpUser(true);
    }
  }, []);

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // 1-indexed (1=Jan, 12=Dec)

  const yearOptions = useMemo(() => {
    const years = [];
    for (let year = 2020; year <= currentYear; year++) {
      years.push(year);
    }
    return years;
  }, [currentYear]);

  const monthOptions = useMemo(() => {
    const months = [
      { value: 1, label: "January" },
      { value: 2, label: "February" },
      { value: 3, label: "March" },
      { value: 4, label: "April" },
      { value: 5, label: "May" },
      { value: 6, label: "June" },
      { value: 7, label: "July" },
      { value: 8, label: "August" },
      { value: 9, label: "September" },
      { value: 10, label: "October" },
      { value: 11, label: "November" },
      { value: 12, label: "December" },
    ];

    if (selectedYear === currentYear) {
      return months.filter((m) => m.value < currentMonth);
    }

    return months;
  }, [selectedYear, currentYear, currentMonth]);

  const handleFetchPayout = async () => {
    if (!selectedYear || selectedMonth === "") {
      alert("Please select both month and year to view payout data");
      return;
    }

    try {
      setLoading(true);
      setPayoutError(null);
      const token = Token.get();

      const response = await axios.get(
        getTeamPayout(
          typeof teamId === "object" ? teamId.id : teamId,
          Number(selectedYear),
          Number(selectedMonth)
        ),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPayoutData(response.data);
    } catch (error) {
      console.error("PayoutTab: Error fetching payout data:", {
        error: error.message,
        stack: error.stack,
        teamId: teamId,
        selectedYear: selectedYear,
        selectedMonth: selectedMonth
      });
      setPayoutError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
    setPayoutData(null);
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
    setSelectedMonth("");
    setPayoutData(null);
  };

  const handleDownloadPDF = (teamPayout) => {
    if (!teamPayout?.data) {
      alert("No payout data available to download");
      return;
    }

    const doc = new jsPDF();
    const { teamDetails, summary, chargerSummary } = teamPayout.data;
    const acc = teamDetails.payoutAccounts?.[0];

    const selectedMonthLabel =
      monthOptions.find((m) => m.value === selectedMonth)?.label || "";
    const invoicePeriod = `${selectedMonthLabel} ${selectedYear}`;

    doc.setFontSize(16);
    doc.text("Payout Invoice", 14, 20);

    doc.setFontSize(11);
    let y = 40;
    doc.text(`Beneficiary: ${acc?.beneficiary_name || "-"}`, 14, y);
    doc.text(`Business: ${acc?.business_name || "-"}`, 14, y + 6);
    doc.text(`Business Type: ${acc?.business_type || "-"}`, 14, y + 12);
    doc.text(`Phone: ${acc?.phone || "-"}`, 14, y + 18);
    doc.text(`Email: ${acc?.email || "-"}`, 14, y + 24);
    doc.text(`GST: ${acc?.gst || "-"}`, 14, y + 30);

    doc.setFontSize(11);
    let rightX = 140;
    doc.text(
      `Invoice Date: ${new Date().toLocaleDateString("en-IN")}`,
      rightX,
      y
    );
    doc.text(`Team: ${teamDetails?.name || "-"}`, rightX, y + 6);
    doc.text(`Period: ${invoicePeriod}`, rightX, y + 12);

    autoTable(doc, {
      startY: 85,
      head: [
        [
          "Transactions",
          "Chargers",
          "kWh",
          "Collection",
          "GST Charges",
          "PG Charges",
          `${domainConfig.name} Charges`,
          "Total Payout",
        ],
      ],
      body: [
        [
          summary.totalTransactions || 0,
          summary.totalChargers || 0,
          summary.totalKW ? summary.totalKW.toFixed(3) : '0.000',
          `Rs ${(summary.totalCollectionWithoutPGC - (summary.totalTaxAmount || 0) - (summary.totalPGCharges || 0)).toFixed(2)}`,
          `Rs ${(summary.totalTaxAmount || 0).toFixed(2)}`,
          `Rs ${(summary.totalPGCharges || 0).toFixed(2)}`,
          `Rs ${summary.totalAmikServiceCharges ? summary.totalAmikServiceCharges.toFixed(2) : '0.00'}`,
          `Rs ${summary.totalPayoutAmount ? summary.totalPayoutAmount.toFixed(2) : '0.00'}`,
        ],
      ],
      styles: {
        fontSize: 9,
        halign: "left",
      },
      headStyles: {
        halign: "left",
      },
    });

    const chargerRows = Object.entries(chargerSummary).map(([id, c]) => [
      id,
      c.charger_type || '',
      (c.totalKW ? c.totalKW.toFixed(3) : '0.000') + " kWh",
      `Rs ${(c.totalCollectionWithoutPGC - (c.totalTaxAmount || 0) - (c.totalPGCharges || 0)).toFixed(2)}`,
      `Rs ${(c.totalTaxAmount || 0).toFixed(2)}`,
      `Rs ${(c.totalPGCharges || 0).toFixed(2)}`,
      `Rs ${c.maxAmikServiceCharges ? c.maxAmikServiceCharges.toFixed(2) : '0.00'}`,
    ]);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [
        ["Charger ID", "Type", "kWh", "Actual (Rs)", "GST (Rs)", "PG (Rs)", `${domainConfig.name} Charges (Rs)`],
      ],
      body: chargerRows,
      styles: {
        fontSize: 9,
        halign: "left",
      },
      headStyles: {
        halign: "left",
      },
    });

    doc.save(`Payout_Invoice_${teamDetails.name}_${invoicePeriod}.pdf`);
  };

  const handleDownloadExcel = () => {
    if (!payoutData?.data || !payoutData.data.length) {
      alert("No payout data available to download");
      return;
    }

    // Prepare data for Excel
    const excelData = payoutData.data.map((item) => {
      const teamPayout = item.payout;
      const teamDetails = teamPayout.data.teamDetails;
      const summary = teamPayout.data.summary;
      const account = teamDetails.payoutAccounts?.[0] || {};

      return {
        "Beneficiary Name": account.beneficiary_name || "",
        "Beneficiary Email ID": account.email || "",
        "Beneficiary Account Number": account.account_no || "",
        IFSC: account.ifsc_code || "",
        "Transaction Type": "NEFT",
        "Debit Account Number": "",
        "Transaction Date": new Date().toLocaleDateString("en-IN"),
        "Total Collection": summary.totalCollectionWithoutPGC ? summary.totalCollectionWithoutPGC.toFixed(2) : '0.00',
        "Collection without GST and PG Charges": summary.totalCollectionWithoutPGC
          ? (summary.totalCollectionWithoutPGC - (summary.totalTaxAmount || 0) - (summary.totalPGCharges || 0)).toFixed(2)
          : '0.00',
        "GST Charges": (summary.totalTaxAmount || 0).toFixed(2),
        "PG Charges": (summary.totalPGCharges || 0).toFixed(2),
        [`${domainConfig.name} Service Charges`]: summary.totalAmikServiceCharges ? summary.totalAmikServiceCharges.toFixed(2) : '0.00',
        Payout: summary.totalPayoutAmount ? summary.totalPayoutAmount.toFixed(2) : '0.00',
        Currency: "INR",
        Remarks: `Payout for ${teamDetails.name} - ${monthOptions.find((m) => m.value === selectedMonth)?.label
          } ${selectedYear}`,
      };
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payouts");

    // Generate Excel file and trigger download
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Payouts_${selectedMonth}_${selectedYear}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box>
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Payout Management
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel id="year-select-label">Year</InputLabel>
            <Select
              labelId="year-select-label"
              value={selectedYear}
              label="Year"
              onChange={handleYearChange}
            >
              {yearOptions.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120 }} disabled={!selectedYear}>
            <InputLabel id="month-select-label">Month</InputLabel>
            <Select
              labelId="month-select-label"
              value={selectedMonth}
              label="Month"
              onChange={handleMonthChange}
            >
              {monthOptions.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleFetchPayout}
            disabled={!selectedMonth || !selectedYear || loading}
          >
            {loading ? <CircularProgress size={24} /> : "Fetch Payout Data"}
          </Button>

          {payoutData?.data && payoutData.data.length > 0 && (
            <Button
              variant="outlined"
              color="success"
              onClick={handleDownloadExcel}
            >
              Download Excel
            </Button>
          )}
        </Stack>

        {payoutError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Unable to load payout data: {payoutError.message || "Please try again later"}
          </Alert>
        )}
      </Card>

      {payoutData?.data && payoutData.data.length > 0 ? (
        <Box>
          <Typography variant="h6" gutterBottom>
            Payout Summary for{" "}
            {monthOptions.find((m) => m.value === selectedMonth)?.label}{" "}
            {selectedYear}
          </Typography>

          {payoutData.data.map((item) => {
            const teamPayout = item.payout;
            const teamDetails = teamPayout.data.teamDetails;
            const summary = teamPayout.data.summary;

            // Safety check for summary object
            if (!summary) {
              return (
                <Card key={item.teamId} sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6">{teamDetails.name}</Typography>
                  <Alert severity="warning">Summary data not available</Alert>
                </Card>
              );
            }

            return (
              <Card key={item.teamId} sx={{ p: 3, mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">{teamDetails.name}</Typography>
                  <Chip
                    label={
                      (summary.totalPayoutAmount || 0) > 0 ? "Payable" : "No Payout"
                    }
                    color={
                      (summary.totalPayoutAmount || 0) > 0 ? "primary" : "default"
                    }
                  />
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      <strong>Total Transactions:</strong>{" "}
                      {summary.totalTransactions || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      <strong>Total Collection:</strong> ₹
                      {(summary.totalCollectionWithoutPGC || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      <strong>{domainConfig.name} Service Charges:</strong> ₹
                      {summary.totalAmikServiceCharges ? summary.totalAmikServiceCharges.toFixed(2) : '0.00'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      <strong>Total Payout:</strong> ₹
                      {summary.totalPayoutAmount ? summary.totalPayoutAmount.toFixed(2) : '0.00'}
                    </Typography>
                  </Grid>
                </Grid>

                {Object.keys(teamPayout.data.chargerSummary).length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                      Charger Breakdown:
                    </Typography>
                    {Object.entries(teamPayout.data.chargerSummary).map(
                      ([chargerId, chargerData]) => (
                        <Box
                          key={chargerId}
                          sx={{
                            mt: 1,
                            p: 1,
                            backgroundColor: "grey.100",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="body2">
                            <strong>{chargerId}:</strong> ₹
                            {(chargerData.totalCollectionWithoutPGC || 0).toFixed(2)} (Total Collection) |{" "}
                            {chargerData.totalKW ? chargerData.totalKW.toFixed(3) : '0.000'} kWh |{" "}
                            ₹ {chargerData.maxAmikServiceCharges ? chargerData.maxAmikServiceCharges.toFixed(2) : '0.00'}{" "}
                            (Amik)
                          </Typography>
                        </Box>
                      )
                    )}
                  </>
                )}

                {/* Finance Config — visible to GP users only */}
                {isGpUser && (
                  <FinanceConfigSection financeConfig={teamDetails.financeConfig} />
                )}

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => handleDownloadPDF(teamPayout)}
                  sx={{ mt: 2 }}
                  disabled={(summary.totalPayoutAmount || 0) === 0}
                >
                  Download PDF
                </Button>
              </Card>
            );
          })}
        </Box>
      ) : payoutData?.data && payoutData.data.length === 0 ? (
        <Alert severity="info">
          No payout data available for the selected period.
        </Alert>
      ) : null}
    </Box>
  );
};
